// Never import anything from this file to use trpc queries or mutations on the frontend
// Use the generated hooks instead
// https://trashlab.atlassian.net/wiki/spaces/TL/pages/1110110513/tRPC+Frontend+Hooks
import React from 'react';
import { QueryClient, QueryKey } from '@tanstack/react-query';
import type { AppRouter } from '@trashlab/api/src/library/server/routers/_app';
import type { NoSuperjsonRouter } from '@trashlab/api/src/library/server/routers/no-superjson';
import {
  CreateTRPCProxyClient,
  createTRPCProxyClient,
  httpLink,
  loggerLink,
} from '@trpc/client';
import { CreateTRPCReact, createTRPCReact } from '@trpc/react-query';
import { transformer } from '@trashlab/api/transformer';

export const SHA_QUERY_KEY = 'getShortGitSHA';

export function getBaseUrl() {
  const port = process.env.PORT ?? '3000';

  if (typeof window !== 'undefined') {
    // browser should use relative path
    return '';
  }

  // // reference for render.com
  const maybeRenderHost = process.env.RENDER_INTERNAL_HOSTNAME;
  if (maybeRenderHost) {
    return `https://${maybeRenderHost}:${port}`;
  }

  // // assume localhost
  // return `http://localhost:${port}`;
  return process.env.NEXT_PUBLIC_APP_HOST ?? '';
}

export const baseUrlToUse = `${getBaseUrl()}/api/trpc`;
export const noSuperjsonBaseUrlToUse = `${getBaseUrl()}/api/trpc-no-superjson`;

const links = [
  // adds pretty logs to your console in development and logs errors in production
  loggerLink({
    enabled: (options) => {
      return (
        process.env.NODE_ENV === 'development' ||
        (options.direction === 'down' && options.result instanceof Error)
      );
    },
  }),
];

links.push(
  httpLink({
    url: baseUrlToUse,
  }),
);

const noSuperjsonLinks = [
  loggerLink({
    enabled: (options) => {
      return (
        process.env.NODE_ENV === 'development' ||
        (options.direction === 'down' && options.result instanceof Error)
      );
    },
  }),
];

noSuperjsonLinks.push(
  httpLink({
    url: noSuperjsonBaseUrlToUse,
  }),
);

// Queries the post-mutation refresh skips. Each entry must be a real tRPC path
// (see packages/api/src/generated/hooks.ts); mutations that change one of these
// must invalidate it explicitly.
export const globalQueries = [
  SHA_QUERY_KEY,
  'identity.getOrganization',
  'identity.getOrCreateUserSettingsByKeys',
  'consumer.queries.listCartsForStatus',
  'quote.queries.admin.count',
  'portal.queries.possibleAccounts',
  'commsCenter.queries.getInProgressComm',
  'commsCenter.queries.getUnreadCommNotifications',
  'notification.queries.notifications',
  'notification.queries.unreadNotificationsCount',
  'notification.queries.notificationSettings',
  'time.user.currentShift',
  'time.user.currentBreak',
  'changelog.queries.getVisibleChangelogEntries',
  'changelog.queries.getUnreadChangelogCount',
  'betaFeatures.getForOrganization',
  'internalComms.queries.getUnreadMessageCounts',
  'internalComms.queries.getChatrooms',
  'internalComms.queries.getChatroomMemberTokens',
  'internalComms.queries.getChatroomMembersInHuddle',
  'internalComms.queries.getChatroomMessages',
  'internalComms.queries.getPinnedChatroomMessages',
];

export function isGlobalQueryKey(queryKey: QueryKey) {
  if (Array.isArray(queryKey)) {
    const key = queryKey[0];

    if (Array.isArray(key)) {
      return globalQueries.includes(key.join('.'));
    }
  }
  return false;
}

export function invalidateQueries(
  queryClient: QueryClient,
  type: 'global' | 'non-global' | 'all',
) {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      if (type === 'global') {
        return isGlobalQueryKey(query.queryKey);
      }

      if (type === 'non-global') {
        return !isGlobalQueryKey(query.queryKey);
      }

      return true;
    },
  });
}

// Never import this in the frontend, use the generated hooks instead
const noSuperjsonTrpcContext = React.createContext<any>(null);

export const trpc: CreateTRPCReact<AppRouter, unknown, null> =
  createTRPCReact<AppRouter>({
    overrides: {
      useMutation: {
        async onSuccess(options) {
          const res = (await options.originalFn()) as
            | undefined
            | { skipInvalidation?: boolean };

          if (!res?.skipInvalidation) {
            await invalidateQueries(options.queryClient, 'non-global');
          }
        },
      },
    },
  });

export const trpcAny = trpc as any;

export const noSuperjsonTrpc: CreateTRPCReact<
  NoSuperjsonRouter,
  unknown,
  null
> = createTRPCReact<NoSuperjsonRouter>({
  context: noSuperjsonTrpcContext,
});

export const noSuperjsonTrpcAny = noSuperjsonTrpc as any;

export const vanillaTrpc: CreateTRPCProxyClient<AppRouter> =
  createTRPCProxyClient<AppRouter>({
    links,
    transformer,
  });

export const vanillaNoSuperjsonTrpc: CreateTRPCProxyClient<NoSuperjsonRouter> =
  createTRPCProxyClient<NoSuperjsonRouter>({
    links: noSuperjsonLinks,
  });
